import React, { useState, useEffect, useCallback } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, Alert, Card, CardBody, Row, Col, Badge, Input } from "reactstrap";
import { pitti_plans } from '../Config/new-pitti-plans';
import debounce from 'lodash.debounce';
import ExistingPlanChoice from './ExistingPlanChoice';

const SubscribePittiPlanModal = ({ open, onClose, organization, onSubmit, onToggleSubscription }) => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscriptionPaused, setIsSubscriptionPaused] = useState(false);
  const [existingPlanAction, setExistingPlanAction] = useState(null);
  const needsChoice = organization?.is_premium && !existingPlanAction;

  // Set the selected plan to the current plan if organization is premium
  useEffect(() => {
    if (open && organization?.is_premium && organization?.plan_details?.id !== undefined) {
      const currentPlan = pitti_plans.find(plan => plan.id === organization.plan_details.id);
      if (currentPlan) {
        setSelectedPlan(currentPlan);
      }
    }
  }, [open, organization]);

  // Sync subscription paused state
  useEffect(() => {
    if (open && organization) {
      setIsSubscriptionPaused(organization.is_subscription_paused || false);
    }
  }, [open, organization]);

  const handleSubmit = async () => {
    if (!selectedPlan || needsChoice) return;

    setIsSubmitting(true);
    try {
      await onSubmit(organization, selectedPlan.id, existingPlanAction);
      setSelectedPlan(null);
      setExistingPlanAction(null);
      onClose();
    } catch (e) {
      console.error('Error subscribing to plan:', e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedPlan(null);
    setExistingPlanAction(null);
    onClose();
  };

  // Debounced API call for toggle subscription
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedApiCall = useCallback(
    debounce(async (org, newPausedState) => {
      if (!onToggleSubscription) return;

      try {
        await onToggleSubscription(org, newPausedState);
      } catch (error) {
        console.error('Error toggling subscription:', error);
      }
    }, 500),
    [onToggleSubscription]
  );

  const handleToggleSubscription = () => {
    if (!organization?.is_premium || !onToggleSubscription) return;

    // Update UI immediately
    const newPausedState = !isSubscriptionPaused;
    setIsSubscriptionPaused(newPausedState);

    // Debounce the API call
    debouncedApiCall(organization, newPausedState);
  };

  return (
    <Modal isOpen={open} toggle={handleClose} centered size="xl">
      <ModalHeader toggle={handleClose}>
        <span style={{ fontSize: "18px", fontWeight: "700" }}>
          Subscribe Plan
        </span>
      </ModalHeader>
      <ModalBody>
        {organization && (
          <Alert color="info" className="mb-3">
            <div><strong>Organization:</strong> {organization.name || 'N/A'}</div>
            <div><strong>Website:</strong> {organization.website || 'N/A'}</div>
            <div><strong>Type:</strong> {organization.type || 'N/A'}</div>
            <div><strong>Current Premium Status:</strong> {organization.is_premium ? 'Yes' : 'No'}</div>
          </Alert>
        )}

        <ExistingPlanChoice organization={organization} value={existingPlanAction} onChange={setExistingPlanAction} />

        {/* Subscription Toggle - Only show for premium organizations */}
        {organization?.is_premium && (
          <div className="d-flex align-items-center justify-content-between mb-3 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
            <div>
              <strong style={{ fontSize: '14px' }}>Subscription Status:</strong>
              <span style={{ marginLeft: '8px', fontSize: '14px', fontWeight: 'bold', color: isSubscriptionPaused ? '#856404' : '#2e7d32' }}>
                {isSubscriptionPaused ? 'Paused' : 'Active'}
              </span>
            </div>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="subscriptionToggle"
                checked={!isSubscriptionPaused}
                onChange={handleToggleSubscription}
                style={{ cursor: 'pointer', width: '48px', height: '24px' }}
              />
              <label className="form-check-label ms-2" htmlFor="subscriptionToggle" style={{ fontSize: '14px', fontWeight: '500' }}>
                {isSubscriptionPaused ? 'Resume Subscription' : 'Pause Subscription'}
              </label>
            </div>
          </div>
        )}

        <h5 className="mb-3" style={{ fontWeight: "600" }}>Pitti Plans</h5>

        <Row>
          {pitti_plans.map((plan) => {
            const isCurrentPlan = organization?.is_premium && organization?.plan_details?.id === plan.id;
            return (
              <Col key={plan.id} xs={12} md={6} lg={3} className="mb-3">
                <Card
                  style={{
                    cursor: 'pointer',
                    border: selectedPlan?.id === plan.id ? '2px solid #007bff' : '1px solid #ddd',
                    backgroundColor: selectedPlan?.id === plan.id ? '#f0f8ff' : 'white',
                    height: '100%',
                    transition: 'all 0.2s ease',
                  }}
                  onClick={() => setSelectedPlan(plan)}
                >
                  <CardBody className="d-flex flex-column">
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-start">
                        <h6 className="mb-1" style={{ fontWeight: "700", fontSize: '16px' }}>
                          {plan.name}
                        </h6>
                        {isCurrentPlan && (
                          <Badge color="success" style={{ fontSize: '10px' }}>Current</Badge>
                        )}
                      </div>
                      <div style={{ fontSize: '28px', fontWeight: '700', color: '#007bff', marginTop: '8px' }}>
                        {plan.currency}{plan.amount}
                        <span style={{ fontSize: '14px', fontWeight: '400', color: '#666' }}>
                          /{plan.type}
                        </span>
                      </div>
                    </div>

                    <div className="flex-grow-1" style={{ fontSize: '13px' }}>
                      <div className="mb-2">
                        <strong>Seats:</strong> {plan.seats}
                      </div>
                      <div className="mb-2">
                        <strong>Credits:</strong> {plan.max_credits}
                      </div>
                      <div className="mb-2">
                        <strong>CRM Allowed:</strong>{' '}
                        <Badge color={plan.crm_included ? 'success' : 'secondary'} style={{ fontSize: '11px' }}>
                          {plan.crm_included ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                    </div>

                    {isCurrentPlan && (
                      <Button
                        color="primary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedPlan(plan);
                          handleSubmit();
                        }}
                        style={{ fontSize: '12px' }}
                      >
                        Renew Current Plan
                      </Button>
                    )}
                  </CardBody>
                </Card>
              </Col>
            );
          })}
        </Row>

        {!selectedPlan && (
          <div className="text-muted text-center mt-3" style={{ fontSize: '14px' }}>
            Please select a plan to continue
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <button
          className='btn btn-outline-dark'
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={isSubmitting || !selectedPlan || needsChoice}
        >
          {isSubmitting ? (
            <Spinner size="sm" color="light" />
          ) : (
            "Subscribe"
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SubscribePittiPlanModal;
